/**
 * Utility functions for handling file downloads
 */

export const downloadFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* Filenames are the CAS Registry Number. CAS is globally unique by
   IUPAC convention, while product-name slugs collide ("Trenbolone
   Enanthate" vs "Trenbolone Acetate" both reduce to chemistry-free
   slugs). scripts/generate-product-pdfs.mjs writes one SDS + one CoA
   per active product to public/downloads/{sds,coa}/<cas>.pdf, so
   "does the file exist?" reduces to "does the product have a CAS?".
   We keep the slug-based filenames for the four originals
   (amoxicillin-trihydrate, atorvastatin-calcium, metformin-hcl,
   ivermectin) as back-compat for any existing bookmark or indexed
   link. */
/* Identical algorithm to scripts/generate-product-pdfs.mjs slugify() so
   the slug-named PDF the generator writes lines up with the URL we emit
   here. Strip filesystem-/URL-unsafe punctuation so product names like
   "Palladium on Carbon (10% Pd/C, wet)" don't produce a slug with "/"
   or "%". Collapse runs of "-" so multiple stripped chars don't leave
   a double-hyphen. */
export const productSlug = (productName: string): string =>
  productName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[()/\\,%:*?"<>|#&]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

export const hasSDS = (_productName: string, casNumber?: string): boolean =>
  !!casNumber;

export const hasCOA = (_productName: string, casNumber?: string): boolean =>
  !!casNumber;

export const sdsUrl = (_productName: string, casNumber?: string): string | null =>
  casNumber ? `/downloads/sds/${casNumber}-sds.pdf` : null;

export const coaUrl = (_productName: string, casNumber?: string): string | null =>
  casNumber ? `/downloads/coa/${casNumber}-coa.pdf` : null;

export const downloadSDS = (casNumber: string, productName: string) => {
  const url = sdsUrl(productName, casNumber);
  if (url) {
    downloadFile(url, `${productSlug(productName)}-${casNumber}-sds.pdf`);
  } else {
    const subject = `SDS request: ${productName}`;
    window.location.href = `/contact?subject=${encodeURIComponent(subject)}`;
  }
};

export const downloadCOA = (casNumber: string, productName: string) => {
  const url = coaUrl(productName, casNumber);
  if (url) {
    downloadFile(url, `${productSlug(productName)}-${casNumber}-coa.pdf`);
  } else {
    const subject = `CoA request: ${productName}`;
    window.location.href = `/contact?subject=${encodeURIComponent(subject)}`;
  }
};

export const downloadCatalog = () => {
  const filename = 'longshine-catalog-2026.pdf';
  const url = `/downloads/catalog/${filename}`;
  downloadFile(url, filename);
};

export const downloadCapabilitiesBrochure = () => {
  const filename = 'capabilities-brochure.pdf';
  const url = `/downloads/brochures/${filename}`;
  downloadFile(url, filename);
};

export const downloadCertificates = () => {
  const filename = 'iso-certificate.pdf';
  const url = `/downloads/certificates/${filename}`;
  downloadFile(url, filename);
};

export const exportProductCatalog = (category?: string) => {
  const categorySlug = category ? category.toLowerCase().replace(/\s+/g, '-') : 'all-products';
  const filename = `${categorySlug}-catalog.pdf`;
  const url = `/downloads/catalog/longshine-catalog-2026.pdf`; // Use main catalog for now
  downloadFile(url, filename);
};